import { plainToClass } from "class-transformer";
import { IFilter, InitializeFilterDropdown } from "../../../shared/scripts/dropdown-functions";
import { getDisplayDate } from "../../../shared/scripts/date-functions";
import { TableColumnGroup, TableColumnVisibilityControllerV2 } from "../../../shared/scripts/table-column-visibility";
import _ from 'lodash';

import { JsonItem, ListItem, ListInformation, PickListItem, AppUserExtended, CombineDisplayOption, IControllerFilter } from "./list-management.objects";
import { ListTables } from "./list-management.tables";
import { FilterTab } from "./list-management.filters";
import { Section, SectionField } from "./list-management.form-sections";
import { UserPermissionSelctor } from "./special-case-sections/UserPermissionSelection";
import { DeliverableSubcategorySelection, DeliverableTrlDropdown, DeliverableStageSelection } from './special-case-sections/DeliverablesSelection';
import { CenterCoreProjectSelection } from "./special-case-sections/CenterCoreProjectSelection";
import { ListSelection } from './special-case-sections/ListSelection';
import { FileUpload } from "./special-case-sections/FileUpload";

declare var $: any;

export class ListController implements IControllerFilter {
    id: string; // used to identify html elements (table, modal, buttons)
    propId: string; // unique Id for item in list
    displayName: string; // Display Name for title
    handler: string //Naming Convention for GET/POST/DELETE routes
    pickLists: JsonItem[]; // Pick Lists used for the list
    listItems: ListItem[]; // combined entities
    listTables: ListTables;
    filterTabs: FilterTab[];
    addNewModalId: string;
    addNewController: AddNewController;
    defaultItem: any;
    editModalId: string;
    editController: EditController;
    listInformation: ListInformation;

    welcomeEmailController: WelcomeEmailController;

    constructor(info: ListInformation) {
        this.id = info.id;
        this.displayName = info.displayName;
        this.handler = info.handler;
        this.filterTabs = info.filterTabs;
        this.propId = info.propId;
        this.defaultItem = info.defaultItem;

        this.addNewModalId = info.addNewModalId;
        this.addNewController = info.addNewController;

        this.editModalId = info.editModalId;
        this.editController = info.editController;

        this.listInformation = info;
        this.welcomeEmailController = new WelcomeEmailController(info.id, "SendWelcomeEmails", "welcome-email-modal");

        this.initialize(info);
    }

    async initialize(info: ListInformation) {
        // Show Loading
        this.loadingDisplay(true);

        // Get Data
        let result: any[] = await this.getListItems();
        if (result["items"]) {
            this.listItems = plainToClass(ListItem, <ListItem[]>result["items"]);
        }
        if (result["pickLists"]) {
            this.pickLists = plainToClass(JsonItem, <JsonItem[]>result["pickLists"]);
        }

        // Filters & Template
        this.createTemplateForController();

        // Table
        this.listTables = new ListTables(this.id, info, this.listItems, this.editController.sections);

        // Hide Loading
        this.loadingDisplay(false);
    }

    async getListItems() {
        let result: any;
        try {
            result = await $.ajax({
                url: `/secure/list-management/list-management?handler=${this.handler}`,
                method: "GET",
                headers: { "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val() },
                cache: false
            });
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    getMultiSelectString(pipeSeparatedIds: string, pickListName: string): string {
        if (!pipeSeparatedIds) {
            return "";
        }

        let stringArrIds: string[] = pipeSeparatedIds.split("|");
        stringArrIds = _.uniq(stringArrIds);
        let pickList: PickListItem[] = this.pickLists.find(item => item.Id == pickListName)?.Json;

        if (!stringArrIds || !pickList) {
            return pipeSeparatedIds;
        }

        let returnStrArray: string[] = [];
        let returnStr: string = "";

        for (let i = 0; i < stringArrIds.length; i++) {
            let numId: number = parseInt(stringArrIds[i]);
            if (numId) {
                let foundItem = pickList.find(item => item.Id == numId);
                if (foundItem) {
                    returnStrArray.push(foundItem.Value)
                }
            }
        }

        returnStrArray.sort((x1: string, x2: string) => x1.localeCompare(x2));
        returnStr = returnStrArray.join(", ");

        return returnStr;
    }

    getDisplayStatus(statusId: number): string {
        if (statusId == 1) {
            // Active
            return "<div class='revampp-active-block'>Active</div>";
        }
        else if (statusId == 2) {
            // Inactive
            return "<div class='revampp-inactive-block'>Inactive</div>";
        }
        else if (statusId == 3) {
            // Hidden
            return "<div class='revampp-hidden-block'>Hidden</div>";
        }
        else if (statusId == 4) {
            // Awaiting Approval 
            return "<div class='revampp-review-block'>Review</div>";
        }
        else {
            return "<div class='revampp-unknown-block'>Unknown</div>";
        }
    }

    getTrueFalseDisplay(value: boolean): string {
        if (value) {
            return "<div class='revampp-active-block'>Yes</div>";
        }
        else {
            return "<div class='revampp-inactive-block'>No</div>";
        }
    }

    getDisplayContent(dataArr: any[], propName: string, pickListName: string = ""): string {
        let content = dataArr.map(item => _.get(item, propName));
        content = _.uniq(content);
        content.sort();
        let returnContent = "";
        if (pickListName) {
            let returnContentArr = [];
            let pickListItem = this.pickLists.find(list => list.Id == pickListName);
            if (pickListItem) {
                let pickList: PickListItem[] = pickListItem.Json;
                for (let i = 0; i < content.length; i++) {
                    let foundVal = pickList.find(item => item.Id == content[i]);
                    if (!foundVal) {
                        continue;
                    }
                    returnContentArr.push(foundVal.Value);
                }
                returnContentArr.sort();
                returnContent = returnContentArr.join(', ');
            }
        }
        else {
            returnContent = content.join(', ');
        }
        return returnContent;
    }

    getPickListContent(itemId: any, pickListName: string = ""): string {
        let returnContent = "";
        if (itemId == null || itemId == undefined) {
            return returnContent;
        }
        if (pickListName) {
            let pickListItem = this.pickLists.find(list => list.Id == pickListName);
            if (pickListItem) {
                let pickList: PickListItem[] = pickListItem.Json;
                let foundVal = pickList.find(item => item.Id == itemId);
                if (foundVal) {
                    returnContent = foundVal.Value;
                }
            }
        }
        return returnContent;
    }

    getCombinedContent(data: any[], combineDisplayOptions: CombineDisplayOption[]) {
        let returnContent = "";
        if (data.length == 0) {
            return returnContent;
        }

        for (let i = 0; i < data.length; i++) {
            let item = data[i];
            let itemDisplay = "";
            for (let j = 0; j < combineDisplayOptions.length; j++) {
                let options = combineDisplayOptions[j];
                let prop = _.get(item, options.propName);
                if (prop) {
                    let pickList: PickListItem[] = this.pickLists.find(list => list.Id == options.pickListName)?.Json;
                    if (pickList) {
                        let foundVal: PickListItem = pickList.find(target => target.Id.toString() == prop.toString());
                        if (foundVal) {
                            itemDisplay += `${options.prevContent}${foundVal.Value}${options.endContent}`;
                        }
                    }
                }
            }
            returnContent += itemDisplay;

            if (i < (data.length - 1)) {
                returnContent += "<hr class='my-1'>"
            }
        }

        return returnContent;
    }

    getDisplayStringForStringArray(strArr: any[]): string {
        return strArr.join(", ");
    }

    getPotentialNullValue(data: any, propname: string) {
        if (!_.get(data, propname)) {
            return "Unknown";
        }
        return _.get(data, propname);
    }

    loadingDisplay(shouldShowLoading: boolean): void {
        if (shouldShowLoading) {
            $("#loaded-content").addClass("d-none");
            $("#loading-content-container").removeClass("d-none");
        }
        else {
            $("#loading-content-container").addClass("d-none");
            $("#loaded-content").removeClass("d-none");
            // Resize all tables on load
            this.listTables.resizeTable();
        }
    }

    createTemplateForController() {
        // Update Title
        $("#list-title").text(this.displayName);

        // Filter Tabs
        this.generateFilterTabContent();

        // Select Table
        $("#select-table-container").empty().html(
            `<table id="${this.id}-select-table" class="table table-striped selectable-rows w-100" style="width:100%"></table>`
        )

        // Clear Detail Menu Container
        $("#detail-menu-container").empty();

        // Edit Table
        $("#edit-modal-top-title").text(`Selected ${this.displayName}`);
        $("#edit-table-container").empty().html(
            `<table id="${this.id}-edit-table" class="table table-striped selectable-rows w-100" style="width:100%"></table>`
        );

        // Teams Access (for Users only)
        $("#check-teams-access-btn").remove();
        if (this.id == "revampp-users") {
            $("#add-new-container").after(`
                <div class="ml-auto" id="check-teams-access-btn">
                    <button class="btn btn-small revampp-white-btn" onclick="page.openTeamsModal()">Check Teams Access</button>
                </div>
            `)
        }
    }

    generateFilterTabContent(): void {
        // Create new Tab Content
        let tabContent = "";
        let filtersContent = "";
        for (let i = 0; i < this.filterTabs.length; i++) {
            let currentFilter = this.filterTabs[i];
            if (i == 0) {
                tabContent += `<li class="nav-item"><a class="nav-link active" id="${currentFilter.id}-tab" data-toggle="tab" href="#${currentFilter.id}" role="tab" aria-controls="${currentFilter.id}" aria-selected="true">${currentFilter.displayName}</a></li>`
                filtersContent += `<div class="tab-pane fade show active" id="${currentFilter.id}" role="tabpanel" aria-labelledby="${currentFilter.id}-tab"><div class='row'>`
            }
            else {
                tabContent += `<li class="nav-item"><a class="nav-link" id="${currentFilter.id}-tab" data-toggle="tab" href="#${currentFilter.id}" role="tab" aria-controls="${currentFilter.id}" aria-selected="false">${currentFilter.displayName}</a></li>`
                filtersContent += `<div class="tab-pane fade" id="${currentFilter.id}" role="tabpanel" aria-labelledby="${currentFilter.id}-tab"><div class='row'>`
            }

            for (let j = 0; j < currentFilter.filters.length; j++) {
                let filter = currentFilter.filters[j];
                filtersContent += filter.generateFilterHtml();
            }

            filtersContent += `</div></div>`
        }

        // Update Tabs
        $("#list-management-tabs").empty().html(tabContent);
        $("#list-filter-container").empty().html(filtersContent);

        // Generate Dropdown
        for (let i = 0; i < this.filterTabs.length; i++) {
            let currentFilterTab = this.filterTabs[i];
            for (let j = 0; j < currentFilterTab.filters.length; j++) {
                let currentFilter = currentFilterTab.filters[j];
                if (currentFilter.pickListName) {
                    let foundPickList: PickListItem[] = this.pickLists.find(list => list.Id == currentFilter.pickListName).Json;
                    if (foundPickList) {
                        currentFilter.initializeDropdown(this.listItems, this, foundPickList);
                    }
                }
                else {
                    currentFilter.initializeDropdown(this.listItems, this);
                }
            }
        }

        this.filterTabs.forEach(tab => {
            if (tab.columnViewName) {
                $(`#${tab.id}-tab`).on('click', () => {
                    $(`#column-filter-dropdown-${this.id}-table-column-visibility`).val([`${tab.columnViewName}`])
                    $(`#column-filter-dropdown-${this.id}-table-column-visibility`).trigger("change");
                })
            }
        });
    }

    getSelectedFilterTabId(): string {
        let selectedTab = $("#list-management-tabs").find(".active").first();
        return selectedTab.prop("id");
    }

    triggerFilter(): void {
        let filteredItems: ListItem[] = _.cloneDeep(this.listItems);

        this.filterTabs.forEach(tab => {
            tab.filters.forEach(filter => {
                filteredItems = filter.filterItems(filteredItems);
            })
        });

        //this.listTables.updateTable(filteredItems);
        this.listTables.updateFilteredItems(filteredItems);
    }

    openAddNewModal(): void {
        this.addNewController.showLoading(false);
        this.addNewController.initialize(this.pickLists, this.defaultItem);
        $(`#${this.addNewModalId}`).modal('show');
    }

    openEditModal(sectionId: string, useSelected: string = "true"): void {
        // Hide Loading on MOdal
        this.editController.showLoading(false);

        // Update the items in the selected table
        this.listTables.updateEditTable(useSelected == "true");

        // initialize the edit 
        if (useSelected == 'true') {
            this.editController.initialize(this.pickLists, this.listTables.selectedItems, sectionId);
        }
        else {
            this.editController.initialize(this.pickLists, [this.listTables.targetItem], sectionId);
        }

        // show modal
        $(`#${this.editModalId}`).modal('show');
    }

    openWelcomeEmailModal(useSelected: string = "true"): void {
        // Hide loading on modal
        this.welcomeEmailController.showLoading(false);

        // initialize
        if (useSelected == "true") {
            this.welcomeEmailController.initialize(this.pickLists, this.listTables.selectedItems);
        }
        else {
            this.welcomeEmailController.initialize(this.pickLists, [this.listTables.targetItem]);
        }

        // show modal
        $(`#${this.welcomeEmailController.modalIdTarget}`).modal('show');
    }

    async saveAddNewModal() {
        // if it isn't valid, don't save
        let isValid = this.addNewController.validate();
        if (!isValid) {
            return;
        }
        else {
            // show the saving on addNewModal
            this.addNewController.showLoading(true);

            // Get Current Filter Options
            let selectedTab: string = this.getSelectedFilterTabId();
            let currentFilterOptions: any[][] = this.filterTabs.map(tab => {
                return tab.getSelectedValues();
            });

            // save changes to DB (returns new items)
            let result: any[] = await this.addNewController.save();

            // reinitialize
            await this.initialize(this.listInformation);

            this.addNewController.showSaved(true);

            // Set Current Filter Options
            for (let i = 0; i < currentFilterOptions.length; i++) {
                let tabVals = currentFilterOptions[i];
                this.filterTabs[i].setSelectedValues(tabVals);
            }
            $(`#${selectedTab}`).trigger("click");

            // hide loading/close modal
            setTimeout(() => {
                $(`#${this.addNewModalId}`).modal('hide');

                // Don't hide content until modal finishes hiding
                setTimeout(() => {
                    this.addNewController.showSaved(false);
                    this.addNewController.showLoading(false);
                }, 1000)
            }, 1000)
        }
    }

    async saveEditModal() {
        // if it isn't valid, don't save
        let isValid = this.editController.validate();
        if (!isValid) {
            return;
        }
        else {
            // show saving on edit modal
            this.editController.showLoading(true);

            // Get Current Filter Options
            let selectedTab: string = this.getSelectedFilterTabId();
            let currentFilterOptions: any[][] = this.filterTabs.map(tab => {
                return tab.getSelectedValues();
            });

            // saves changes to DB (returns updated items)
            let result: any[] = await this.editController.save();

            // reinitialze
            await this.initialize(this.listInformation);

            // Show Saved
            this.editController.showSaved(true);

            // Set Current Filter Options
            for (let i = 0; i < currentFilterOptions.length; i++) {
                let tabVals = currentFilterOptions[i];
                this.filterTabs[i].setSelectedValues(tabVals);
            }
            $(`#${selectedTab}`).trigger("click");

            // hide loading/close modal
            setTimeout(() => {
                $(`#${this.editModalId}`).modal('hide');

                // Don't hide information until after modal finishes hiding
                setTimeout(() => {
                    this.editController.showLoading(false);
                    this.editController.showSaved(false);
                }, 1000)       
            }, 1000)
        }

    }

    async saveWelcomeEmailModal() {
        // show the saving on addNewModal
        this.welcomeEmailController.showLoading(true);

        // Get Current Filter Options
        let selectedTab: string = this.getSelectedFilterTabId();
        let currentFilterOptions: any[][] = this.filterTabs.map(tab => {
            return tab.getSelectedValues();
        });

        // save changes to DB (returns new items)
        let result: any[] = await this.welcomeEmailController.save();
        this.welcomeEmailController.updatedItems = result;

        // reinitialize
        await this.initialize(this.listInformation);

        this.welcomeEmailController.showSaved(true);

        // Set Current Filter Options
        for (let i = 0; i < currentFilterOptions.length; i++) {
            let tabVals = currentFilterOptions[i];
            this.filterTabs[i].setSelectedValues(tabVals);
        }
        $(`#${selectedTab}`).trigger("click");

        // hide loading/close modal
        setTimeout(() => {
            this.welcomeEmailController.showSaved(false);
            this.welcomeEmailController.showLoading(false);
            this.welcomeEmailController.generateUpdatedBody();
        }, 1000)
    }

}

export class AddNewController {
    id: string;
    title: string;
    submitHandler: string;
    sections: Section[];
    modalIdTarget: string;
    defaultItem: any;

    fileUploadFields: FileUpload[];

    constructor(id: string, title: string, submitHandlerName: string, sections: Section[], modalIdTarget: string) {
        this.id = id;
        this.title = title;
        this.submitHandler = submitHandlerName;
        this.sections = sections;
        this.modalIdTarget = modalIdTarget;
    }

    initialize(pickLists: JsonItem[], defaultItem: any): void {
        // Get & Set HTML for add modal
        let modalContentTarget = $(`#${this.modalIdTarget}`);
        let htmlContent = this.generateHtml();
        modalContentTarget.find(".modal-content").empty().html(htmlContent);

        this.defaultItem = _.cloneDeep(defaultItem);

        // Initialize Section Fields
        this.fileUploadFields = [];

        this.sections.forEach(section => {
            section.fields.forEach(field => {
                field.initialize(pickLists, [this.defaultItem]);
                if (field.type == "file-upload") {
                    let fileUploadField: FileUpload = <FileUpload>field.specialCaseEntity;
                    this.fileUploadFields.push(fileUploadField);
                }
            });
        });
    }

    generateHtml(): string {
        let saveBlock = `
        <div id="add-item-saving" class="col-12 d-none">
            <div class="saving-modal-block text-center d-flex flex-column justify-content-center">
                <div id="save-in-progress-block" class="font-weight-bold font-size24">
                    Saving...
                </div>
                <div id="save-complete-block" class="font-weight-bold font-size24">
                    Saved!
                </div>
            </div>
        </div>`

        // Modal Header
        let html = `<div class="modal-header border-0 pb-0"><button type="button" class="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button></div>`;

        // Modal Body 
        html += `<div class="modal-body border-0 pt-0">`;
        html += `<div class="modal-title">${this.title}</div>`;

        // Form Info (in body)
        let addFormContent = `<div class="sections-block-container">`;
        this.sections.forEach(section => {
            addFormContent += section.generateHtml(false);
        });
        addFormContent += "</div>";
        html += addFormContent;
        html += `</div>`

        // Modal Footer
        let footerContent = `<div class="modal-footer justify-content-start border-0 pt-0">`;
        footerContent += `<button class="greenbtn" type="button" onclick="page.currentListController.saveAddNewModal()">Add New!</button>`;
        footerContent += "</div>";
        html += footerContent;

        let returnedContent = `
        <div id="add-item-content">
            ${html}
        </div>
        ${saveBlock}
        `

        return returnedContent;
    }

    showLoading(isLoading: boolean) {
        if (isLoading) {
            this.showSaved(false);
            $("#add-item-content").addClass("d-none");
            $("#add-item-saving").removeClass("d-none");
        }
        else {
            $("#add-item-content").removeClass("d-none");
            $("#add-item-saving").addClass("d-none");
        }
    }

    showSaved(isSaved: boolean) {
        if (isSaved) {
            $("#save-complete-block").removeClass("d-none");
            $("#save-in-progress-block").addClass("d-none");
        }
        else {
            $("#save-complete-block").addClass("d-none");
            $("#save-in-progress-block").removeClass("d-none");
        }
    }

    validate(): boolean {
        let isValid = true;
        this.sections.forEach(section => {
            section.fields.forEach(field => {
                if (!field.validate()) {
                    isValid = false;
                }
            });
        })
        return isValid;
    }

    getCurrentItem(): any {
        let updatedItem = _.cloneDeep(this.defaultItem);

        let specialCaseSections = ["center-core-project-selection-new", "file-upload"]

        this.sections.forEach(section => {
            section.fields.forEach(field => {
                if (field.validate() && !field.isReadOnly && !specialCaseSections.includes(field.type)) {
                    _.set(updatedItem, field.propertyName, field.getValue())
                }
                else if (field.type == "center-core-project-selection-new") {
                    let specialCase: CenterCoreProjectSelection = <CenterCoreProjectSelection>field.specialCaseEntity;
                    _.set(updatedItem, specialCase.centerPropName, specialCase.getCenterValue());
                    _.set(updatedItem, specialCase.corePropName, specialCase.getCoreValue());
                    _.set(updatedItem, specialCase.projectPropName, specialCase.getProjectValue());
                }
            });
        });

        return updatedItem;
    }

    async save(): Promise<any> {

        let currentItem: any[] = [this.getCurrentItem()];

        let result: any[];
        try {
            result = await $.ajax({
                url: `/secure/list-management/list-management?handler=${this.submitHandler}`,
                method: "POST",
                headers: { "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val() },
                data: { data: JSON.stringify(currentItem) },
            });

            // Upload attachments after entity is saved in DB, so that we have ID
            if (this.fileUploadFields.length > 0) {
                for (let i = 0; i < this.fileUploadFields.length; i++) {
                    let fileUploadField: FileUpload = this.fileUploadFields[i];
                    await fileUploadField.uploadAttachments(result);
                }
            }
            
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }
}

export class EditController {
    id: string;
    title: string;
    submitHandler: string;
    sections: Section[];
    modalIdTarget: string;
    displayPropertyName: string;

    selectedItems: any[];
    updatedItems: any[];
    selectedSection: Section;

    fileUploadFields: FileUpload[];

    constructor(id: string, title: string, submitHandlerName: string, sections: Section[], modalIdTarget: string, displayPropertyName: string) {
        this.id = id;
        this.title = title;
        this.submitHandler = submitHandlerName;
        this.sections = sections;
        this.modalIdTarget = modalIdTarget;
        this.displayPropertyName = displayPropertyName;
    }

    initialize(pickLists: JsonItem[], selectedItems: any[], selectedSectionId: string): void {

        // Update Selected Section
        this.selectedItems = _.cloneDeep(selectedItems);
        this.updatedItems = _.cloneDeep(selectedItems);
        this.selectedSection = this.sections.find(section => section.id == selectedSectionId);

        // Get & Set HTML for add modal
        let modalContentTarget = $(`#${this.modalIdTarget}`);
        let htmlBodyContent = this.generateBodyHtml();
        modalContentTarget.find(".edit-section-container").empty().html(htmlBodyContent);

        let htmlFooterContent = `<button class="greenbtn"" type="button" onclick="page.currentListController.saveEditModal()">Save & Close</button>`;
        modalContentTarget.find(".modal-footer").empty().html(htmlFooterContent);

        // Initialize Section Fields
        this.fileUploadFields = [];
        this.sections.forEach(section => {
            section.fields.forEach(field => {
                field.initialize(pickLists, this.selectedItems);
                if (field.type == "edit-file-upload") {
                    let fileUploadField: FileUpload = <FileUpload>field.specialCaseEntity;
                    this.fileUploadFields.push(fileUploadField);
                }
            });
        });

        this.initializeForm();
        this.updateChangeLog();
    }

    initializeForm(): void {
        // Tab Content 
        $(`#${this.modalIdTarget}`).find("#edit-form-tab").text(this.title);
        $(`#${this.modalIdTarget}`).find("#edit-form-tab").addClass("active");
        $(`#${this.modalIdTarget}`).find("#edit-table-tab").removeClass("active");
        $(`#${this.modalIdTarget}`).find("#edit-form").addClass("show").addClass("active");
        $(`#${this.modalIdTarget}`).find("#edit-table").removeClass("show").removeClass("active");

        $(`#${this.modalIdTarget}`).find(".edit-modal-section-block").on('change keyup', () => {
            this.updateChangeLog();
        });
    }

    showLoading(isLoading: boolean) {
        if (isLoading) {
            this.showSaved(false);
            $(`#${this.modalIdTarget}`).find("#edit-item-content").addClass("d-none");
            $(`#${this.modalIdTarget}`).find("#edit-item-saving").removeClass("d-none");
        }
        else {
            $(`#${this.modalIdTarget}`).find("#edit-item-content").removeClass("d-none");
            $(`#${this.modalIdTarget}`).find("#edit-item-saving").addClass("d-none");
        }
    }

    showSaved(isSaved: boolean) {
        if (isSaved) {
            $(`#${this.modalIdTarget}`).find("#edit-save-complete-block").removeClass("d-none");
            $(`#${this.modalIdTarget}`).find("#edit-save-in-progress-block").addClass("d-none");
        }
        else {
            $(`#${this.modalIdTarget}`).find("#edit-save-complete-block").addClass("d-none");
            $(`#${this.modalIdTarget}`).find("#edit-save-in-progress-block").removeClass("d-none");
        }
    }

    generateBodyHtml(): string {
   
        // Form Content
        let formContent = `<div class="modal-title">Edit ${this.selectedSection.title}</div>`;
        formContent += `<div class='edit-modal-section-block'>`;
        if (this.selectedSection) {
            this.selectedSection?.fields.forEach(field => {
                formContent += field.generateHtml();
            })
        }
        formContent += "</div>";

        // Change Log
        let changeLog = "";
        if (!this.selectedSection.noChangeLog) {
            changeLog = `<div class="modal-title">Change Log</div>`;
            changeLog += `<div class='edit-modal-change-log-block' id="change-log-block-${this.id}">`;

            let html = `
            <div class="row">
                <div class="col-12 col-md-6 col-lg-5">
                    ${formContent}
                </div>
                <div class="col-12 col-md-6 col-lg-7">
                    ${changeLog}
                </div>
            </div>
            `

            return html;
        }
        else {
            let html = `
            <div>
                ${formContent}
            </div>
            `

            return html;
        }
    }

    updateChangeLog(): void {
        let changeLogBlock = $(`#change-log-block-${this.id}`);

        let content = `<div id="change-log-accordion">`;

        // Special Case Types with a change log
        let updateChangeLogSpecialCaseTypes = [
            "permissions-selection",
            "deliverable-trl-dropdown",
            "deliverable-subcategory-dropdown",
            "deliverable-stage-dropdown",
            "center-core-project-selection",
            "list-selection",
            "edit-file-upload"
        ];

        for (let i = 0; i < this.selectedItems.length; i++) {
            let item = this.selectedItems[i];
            let updatedItem = this.updatedItems[i];
            let displayName = _.get(item, this.displayPropertyName);
            let cardContent = ``;
            cardContent += ``;

            let isChanged = false;
            let changedBubbles = ``;
            let changedTableContent = ``

            for (let j = 0; j < this.selectedSection.fields.length; j++) {
                let field = this.selectedSection.fields[j];
                if (field.specialCaseEntity && !updateChangeLogSpecialCaseTypes.includes(field.type)) {
                    if (field.checkChanged()) {
                        _.set(updatedItem, field.propertyName, field.getValue())
                    }
                    continue;
                }
                else if (field.type == "permissions-selection") {
                    let specialCaseField: UserPermissionSelctor = <UserPermissionSelctor>field.specialCaseEntity;
                    let addedPermissions: string[] = field.getValue();
                    let removedPermissions: string[] = specialCaseField.getRemovedPermissions();
                    let unspecifiedPermissions = specialCaseField.getUnspecified();

                    let currentPermissions: string[] = specialCaseField.getCurrentPermissionArr(i);
                    let isPermissionChanged = true;
                    if (addedPermissions.every((val, i, arr) => currentPermissions.includes(val)) && removedPermissions.every((val, i, arr) => !currentPermissions.includes(val))) {
                        isPermissionChanged = false;
                    }

                    if (isPermissionChanged) {
                        isChanged = true;
                        changedBubbles += `<div class="change-prop-bubble">${field.displayName}</div>`;
                        changedTableContent += `
                            <tr>
                                <td class="font-weight-bold">${field.displayName}</td>
                                <td>${specialCaseField.getPreviousDisplayValue(i)}</td>
                                <td>${specialCaseField.getCurrentDisplayValue(i)}</td>
                            </tr>
                            `

                        let newPermissions = [...addedPermissions];
                        unspecifiedPermissions.forEach(permission => {
                            if (currentPermissions.includes(permission)) {
                                newPermissions.push(permission);
                            }
                        });

                        _.set(updatedItem, field.propertyName, newPermissions);
                    }
                }
                else if (field.type == "deliverable-trl-dropdown" || field.type == "deliverable-subcategory-dropdown" || field.type == "deliverable-stage-dropdown") {
                    let specialCaseField;
                    if (field.type == "deliverable-trl-dropdown") {
                        specialCaseField = <DeliverableTrlDropdown>field.specialCaseEntity;
                    }
                    else if (field.type == "deliverable-subcategory-dropdown") {
                        specialCaseField = <DeliverableSubcategorySelection>field.specialCaseEntity;
                    }
                    else if (field.type == "deliverable-stage-dropdown") {
                        specialCaseField = <DeliverableStageSelection>field.specialCaseEntity;
                    }
                    else {
                        continue;
                    }

                    if (field.checkChanged()) {
                        isChanged = true;
                        changedBubbles += `<div class="change-prop-bubble">${field.displayName}</div>`;
                        changedTableContent += `
                            <tr>
                                <td class="font-weight-bold">${field.displayName}</td>
                                <td>${specialCaseField.getPreviousDisplayValue(i)}</td>
                                <td>${specialCaseField.getCurrentDisplayValue()}</td>
                            </tr>
                        `

                        _.set(updatedItem, field.propertyName, field.getValue())
                    }
                }
                else if (field.type == "center-core-project-selection") {
                    let specialCaseField: CenterCoreProjectSelection = <CenterCoreProjectSelection>field.specialCaseEntity;
                    if (field.checkChanged()) {
                        isChanged = true;
                        changedBubbles += `<div class="change-prop-bubble">${field.displayName}</div>`;
                        changedTableContent += `
                            <tr>
                                <td class="font-weight-bold">${field.displayName}</td>
                                <td>${specialCaseField.getPreviousDisplayValue(i)}</td>
                                <td>${specialCaseField.getCurrentDisplayValue()}</td>
                            </tr>
                        `;

                        _.set(updatedItem, specialCaseField.centerPropName, specialCaseField.getCenterValue());
                        _.set(updatedItem, specialCaseField.corePropName, specialCaseField.getCoreValue());
                        _.set(updatedItem, specialCaseField.projectPropName, specialCaseField.getProjectValue());
                    }
                }
                else if (field.type == "list-selection") {
                    let specialCaseField: ListSelection = <ListSelection>field.specialCaseEntity;
                    if (field.checkChanged()) {
                        isChanged = true;
                        changedBubbles += `<div class="change-prop-bubble">${field.displayName}</div>`;
                        changedTableContent += `
                            <tr>
                                <td class="font-weight-bold">${field.displayName}</td>
                                <td>${specialCaseField.getDisplayValue(i)}</td>
                                <td>${specialCaseField.getDisplayValue()}</td>
                            </tr>
                        `;

                        _.set(updatedItem, specialCaseField.propertyName, specialCaseField.getValue());
                    }
                }
                if (updateChangeLogSpecialCaseTypes.includes(field.type)) {
                    continue;
                }

                let isDifferentVal: boolean = false;
                if (!field.specialCaseEntity) {
                    isDifferentVal = field.getValue() != field.storedValues[i];
                }
                else if (field.type == "permissions-selection") {
                    isDifferentVal = isChanged;
                }

                // only considered changed if the values don't match and the field has been changed ()
                if (field.checkChanged() && isDifferentVal) {
                    isChanged = true;
                    changedBubbles += `<div class="change-prop-bubble">${field.displayName}</div>`;
                    changedTableContent += `
                    <tr>
                        <td class="font-weight-bold">${field.displayName}</td>
                        <td>${field.getDisplayValue(i)}</td>
                        <td>${field.getDisplayValue()}</td>
                    </tr>
                    `

                    // update the value
                    _.set(updatedItem, field.propertyName, field.getValue());
                }
                else {
                    // reset value to whatever it currently is
                    _.set(updatedItem, field.propertyName, _.get(item, field.propertyName))
                }

            }

            let changedTable = `
            <div id="change-log-collapse-${i}" class="collapse">
                <div class="revampp-datatable font-size12" id="changed-table-container-${i}">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>Property Name</th>
                                <th>Previous Value(s)</th>
                                <th>New Value(s)</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${changedTableContent}
                        </tbody>
                    </table>
                </div>
            </div>
            `

            let card = `
            <div class="change-log-item">
                <div class="collapsed mb-2 change-log-btn" data-toggle="collapse" data-target="#change-log-collapse-${i}">
                    <div class="change-log-title ${isChanged ? '' : "no-icon"}">${displayName}</div>
                    <div class="change-log-bubbles col-12 row">
                        ${isChanged ? changedBubbles : `<div class="change-prop-bubble no-change">-- No Change --</div>`}
                    </div>
                </div>
                ${isChanged ? changedTable : ''}
            </div>
            `

            content += card;
        }
        content += `</div>`;

        changeLogBlock.empty().html(content);
    }

    async save(): Promise<any> {
        if (this.selectedSection.fields.some(field => field.specialCaseEntity != undefined)) {
            this.updateChangeLog();
        }

        let result: Promise<any>;
        try {
            result = await $.ajax({
                url: `/secure/list-management/list-management?handler=${this.submitHandler}`,
                method: "POST",
                headers: { "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val() },
                data: { data: JSON.stringify(this.updatedItems) },
            });

            if (this.fileUploadFields.length > 0) {
                for (let i = 0; i < this.fileUploadFields.length; i++) {
                    let fileUploadField = this.fileUploadFields[i];
                    await fileUploadField.updateAttachments();
                }
            }
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    validate(): boolean {
        let isValid = true;
        this.selectedSection.fields.forEach(field => {
            if (!field.validate()) {
                isValid = false;
            }
        });
        return isValid;
    }
}

export class WelcomeEmailController {
    id: string;
    submitHandler: string;
    modalIdTarget: string;

    selectedItems: AppUserExtended[] = [];
    updatedItems: AppUserExtended[] = [];

    constructor(id: string, submitHandler: string, modalIdTarget: string) {
        this.id = id;
        this.submitHandler = submitHandler;
        this.modalIdTarget = modalIdTarget;
    }

    initialize(pickLists: JsonItem[], selectedItems: any[]): void {

        // Update Selected Section
        this.selectedItems = _.cloneDeep(selectedItems);
        this.updatedItems = [];

        // Get & Set HTML for add modal
        let modalContentTarget = $(`#${this.modalIdTarget}`);
        let htmlBodyContent = this.generateBodyHtml();
        modalContentTarget.find(".modal-body").empty().html(htmlBodyContent);

        let htmlFooterContent = `<button class="greenbtn" type="button" onclick="page.currentListController.saveWelcomeEmailModal()">Send Welcome Emails</button>`;
        modalContentTarget.find(".modal-footer").empty().html(htmlFooterContent);
    }

    generateBodyHtml(): string {
        let bodyHtml = "";
        for (let i = 0; i < this.selectedItems.length; i++) {
            let currentUser = this.selectedItems[i].AppUser;
            let expandContent = `
                <div id="welcome-email-list-collapse-${i}" class="collapse p-1">
                    <div>
                        <b>Name</b>: ${currentUser.FirstName} ${currentUser.LastName}
                    </div>
                    <div>
                        <b>Email</b>: ${currentUser.Email} 
                    </div>
                    <div>
                        <b>Previously Sent Welcome Email</b>: ${currentUser.SentWelcomeEmail ? `Yes (${getDisplayDate(currentUser.SentWelcomeEmailDate)})` : `No` }
                    </div>
                </div>
            `
            bodyHtml += `
                <div class="welcome-email-list-item">
                    <div class="welcome-email-item-block collapsed" data-toggle="collapse" data-target="#welcome-email-list-collapse-${i}">
                        <div class="welcome-email-block-title">
                            ${currentUser.FirstName} ${currentUser. LastName} (${currentUser.Email})
                        </div>
                    </div>
                    ${expandContent}
                </div>
            `
        }
        let returnHtml = `
        <p>Click the "Send Welcome Emails" button to send welcome emails to the following users:</p>
        <div class="welcome-email-block-list">${bodyHtml}</div>`;
        return returnHtml;
    }

    generateUpdatedBody(): string {
        let bodyHtml = "";
        let hasErrors: boolean = false;
        for (let i = 0; i < this.updatedItems.length; i++) {
            let currentUser = this.updatedItems[i].AppUser;

            let hasWelcomeEmail = currentUser.SentWelcomeEmail;
            let errorMessageBlock = "";
            let errorIcon = "";
            if (!hasWelcomeEmail) {
                errorMessageBlock = `
                    <div>
                        <b class="text-danger">Error</b>: This user was not sent a welcome email because their account is currently inactive.
                    </div>
                `;

                errorIcon = `<span class="pl-1 pr-2"><i class="fa-solid fa-circle-xmark fa-lg text-danger"></i></span>`
                hasErrors = true;
            }
            
            let expandContent = `
                <div id="welcome-email-list-collapse-${i}" class="collapse p-1">
                    ${errorMessageBlock}
                    <div>
                        <b>Name</b>: ${currentUser.FirstName} ${currentUser.LastName}
                    </div>
                    <div>
                        <b>Email</b>: ${currentUser.Email} 
                    </div>
                    <div>
                        <b>Previously Sent Welcome Email</b>: ${currentUser.SentWelcomeEmail ? `Yes (${getDisplayDate(currentUser.SentWelcomeEmailDate)})` : `No`}
                    </div>
                </div>
            `
            bodyHtml += `
                <div class="welcome-email-list-item">
                    <div class="welcome-email-item-block collapsed" data-toggle="collapse" data-target="#welcome-email-list-collapse-${i}">
                        <div class="welcome-email-block-title">
                            ${errorIcon}${currentUser.FirstName} ${currentUser.LastName} (${currentUser.Email})
                        </div>
                    </div>
                    ${expandContent}
                </div>
            `
        }

        let updateMessage = `<p>Successfully sent welcome emails to the following users!</p>`;
        if (hasErrors) {
            updateMessage = `<p class="text-danger">Some users were not sent welcome emails. Please review the list below.</p>`
        }

        let returnHtml = `
            ${updateMessage}
            <div class="welcome-email-block-list">${bodyHtml}</div>
        `;

        let modalContentTarget = $(`#${this.modalIdTarget}`);
        modalContentTarget.find(".modal-body").empty().html(returnHtml);

        let htmlFooterContent = `<button class="greenbtn" type="button" onclick="page.closeAllModals()">Close</button>`;
        modalContentTarget.find(".modal-footer").empty().html(htmlFooterContent);

        return returnHtml;
    }

    showLoading(isLoading: boolean) {
        if (isLoading) {
            this.showSaved(false);
            $("#welcome-email-content").addClass("d-none");
            $("#welcome-email-saving").removeClass("d-none");
        }
        else {
            $("#welcome-email-content").removeClass("d-none");
            $("#welcome-email-saving").addClass("d-none");
        }
    }

    showSaved(isSaved: boolean) {
        if (isSaved) {
            $("#welcome-email-send-complete-block").removeClass("d-none");
            $("#welcome-email-send-progress-block").addClass("d-none");
        }
        else {
            $("#welcome-email-send-complete-block").addClass("d-none");
            $("#welcome-email-send-progress-block").removeClass("d-none");
        }
    }

    async save(): Promise<any> {
        let result: Promise<any>;
        try {
            result = await $.ajax({
                url: `/secure/list-management/list-management?handler=${this.submitHandler}`,
                method: "POST",
                headers: { "RequestVerificationToken": $('input[name="__RequestVerificationToken"]').val() },
                data: { data: JSON.stringify(this.selectedItems) },
            })
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }
}
